// @file Surface store
import window from '@@/bits/global'
import type { Wall } from '@padlet/arvo'
import { defineStore } from 'pinia'
import type { Id, User, WallBackground, WallLayoutAttributes, WallViz } from 'types'
import { PostSize } from 'types'
import { computed, ref } from 'vue'

export const SEARCH_BAR_DEFAULT_TOP = 16

export const useSurfaceStore = defineStore('surface', () => {
  const surfaceVuexStore = window.app?.$store // For gradual conversion to pinia

  // State
  const xSearchBar = ref(false)
  const isSearchBarInEditMode = ref(false)
  const searchBarTop = ref(SEARCH_BAR_DEFAULT_TOP)

  // Getters
  const isScreenshotMode = computed<boolean>(() => surfaceVuexStore?.getters.isScreenshotMode)
  const isOnline = computed(() => surfaceVuexStore?.getters.isOnline)
  const isOverlayVisible = computed<boolean>(() => surfaceVuexStore?.getters.isOverlayVisible ?? false)
  const wallId = computed(() => surfaceVuexStore?.state.wall.id)
  const xHeader = computed(() => surfaceVuexStore?.getters.xHeader)
  const isUserHostingZoomCollaboration = computed<boolean>(() => surfaceVuexStore?.state.isUserHostingZoomCollaboration)
  const headerHeight = computed<number>(() => surfaceVuexStore?.getters.headerHeight)
  const background = computed<WallBackground>(() => surfaceVuexStore?.getters.background)
  const postSize = computed<PostSize>(() => surfaceVuexStore?.getters.postSize)
  const isPostSizeWide = computed<boolean>(() => postSize.value === PostSize.WIDE)
  const isPostSizeStandard = computed<boolean>(() => postSize.value === PostSize.STANDARD)
  const isGrid = computed<boolean>(() => surfaceVuexStore?.getters.isGrid)
  const isMap = computed<boolean>(() => surfaceVuexStore?.getters.isMap)
  const isShelf = computed<boolean>(() => surfaceVuexStore?.getters.isShelf)
  const isStream = computed<boolean>(() => surfaceVuexStore?.getters.isStream)
  const isSectioned = computed<boolean>(() => surfaceVuexStore?.getters.isSectioned)
  const isTimeline = computed<boolean>(() => surfaceVuexStore?.getters.isTimeline)
  const isTimelineV1 = computed<boolean>(() => surfaceVuexStore?.getters.isTimelineV1)
  const isTimelineV2 = computed<boolean>(() => surfaceVuexStore?.getters.isTimelineV2)
  const isCanvas = computed<boolean>(() => surfaceVuexStore?.getters.isCanvas)
  const isCustomBackground = computed<boolean>(() => surfaceVuexStore?.getters.background.is_custom)
  const isBackgroundBlurred = computed<boolean>(() => surfaceVuexStore?.getters.isBackgroundBlurred)
  const isTitleBarTransparent = computed<boolean>(() => surfaceVuexStore?.getters.isTitleBarTransparent)
  const backgroundColor = computed(() => surfaceVuexStore?.getters.backgroundColor)
  const backgroundLuminance = computed<'light' | 'dark'>(() => surfaceVuexStore?.getters.backgroundLuminance)
  const colorScheme = computed<'light' | 'dark'>(() => surfaceVuexStore?.getters.colorScheme)
  const skinAccentColorAsRGB = computed(() => surfaceVuexStore?.getters.skinAccentColorAsRGB)
  const skinPrimaryColorAsRGB = computed(() => surfaceVuexStore?.getters.skinPrimaryColorAsRGB)
  const skinPrimaryVariantColorAsRGB = computed(() => surfaceVuexStore?.getters.skinPrimaryVariantColorAsRGB)
  const skinAccentTextColor = computed(() => surfaceVuexStore?.getters.skinAccentTextColor)
  const isReadOnly = computed(() => surfaceVuexStore?.getters.isReadOnly)
  const isEmbedded = computed<boolean>(() => surfaceVuexStore?.state.isEmbedded)
  const dir = computed<'ltr' | 'rtl'>(() => surfaceVuexStore?.getters.dir)
  const fonts = computed(() => {
    // in SettingsV2, we want the fonts to be in a different order
    const fontIdOrder = [3, 2, 4, 1]
    const resultFonts = fontIdOrder.map((id) =>
      surfaceVuexStore?.state.constants.allFonts.find((font) => font.id === id),
    )
    return resultFonts
  })
  const fontId = computed(() => surfaceVuexStore?.getters.fontId)
  const viz = computed<WallViz>(() => surfaceVuexStore?.getters.viz)
  const format = computed<WallViz>(() => surfaceVuexStore?.getters.format)
  const newPostLocation = computed<'top' | 'bottom'>(() => surfaceVuexStore?.getters.newPostLocation)
  const address = computed<string>(() => surfaceVuexStore?.getters.address)
  const builder = computed<User>(() => surfaceVuexStore?.getters.builder)
  const title = computed<string>(() => surfaceVuexStore?.getters.title)
  const description = computed<string | undefined>(() => surfaceVuexStore?.getters.description)
  const portrait = computed<string>(() => surfaceVuexStore?.getters.portrait)
  const wallCreatedAt = computed<string>(() => surfaceVuexStore?.getters.wallCreatedAt)
  const wallUpdatedAt = computed<string>(() => surfaceVuexStore?.getters.wallUpdatedAt)
  const wallAttributes = computed<Partial<Wall>>(() => surfaceVuexStore?.state.wall)
  const mapThemeId = computed<number>(() => surfaceVuexStore?.state.wall.theme_id)
  const isRefreshPending = computed<boolean>(() => surfaceVuexStore?.state.isRefreshPending)
  const filterProfanity = computed<boolean>(() => surfaceVuexStore?.getters.filterProfanity)
  const domainName = computed<string>(() => surfaceVuexStore?.getters.domainName)
  const namespace = computed<string>(() => surfaceVuexStore?.getters.namespace)
  const publicKey = computed<string>(() => surfaceVuexStore?.getters.publicKey)
  const googleApiKeyForDrive = computed<string>(() => surfaceVuexStore?.state.constants.googleApiKeyForDrive)
  const permalink = computed<string>(() => surfaceVuexStore?.getters.name)
  const links = computed<Record<string, string>>(() => surfaceVuexStore?.getters.links)
  const xReportPanel = computed<boolean>(() => surfaceVuexStore?.getters.xReportPanel)
  const reportedPostId = computed<Id | null>(() => surfaceVuexStore?.getters.reportedPostId)
  const xAuthor = computed<boolean>(() => surfaceVuexStore?.getters.xAuthor)
  const xCommentsUnderPosts = computed<boolean>(
    () =>
      surfaceVuexStore?.getters.isMap === false &&
      surfaceVuexStore?.getters.isTable === false &&
      surfaceVuexStore?.getters.isCanvas === false &&
      surfaceVuexStore?.getters.isTimeline === false,
  )

  const canUseSections = computed<boolean>(() => surfaceVuexStore?.getters.sectionableLayouts.includes(viz.value))
  const isUsedInEducationalContext = computed<boolean>(() => surfaceVuexStore?.getters.isUsedInEducationalContext)
  const user = computed<User>(() => surfaceVuexStore?.getters.user)
  const xFilterProfanitySetting = computed<boolean>(() => surfaceVuexStore?.getters.xFilterProfanitySetting ?? false)
  const allLayouts = computed(() => surfaceVuexStore?.state.constants.allLayouts)
  const allLayoutsByGroup = computed<{
    simple: WallLayoutAttributes[]
    advanced: WallLayoutAttributes[]
  }>(() => surfaceVuexStore?.state.constants.allLayoutsByGroup)
  const breakoutSectionId = computed(() => surfaceVuexStore?.state.breakoutSectionId)
  const isSectionBreakout = computed<boolean>(() => breakoutSectionId.value !== null)
  const isSubmissionRequest = computed<boolean>(() => surfaceVuexStore?.state.isSubmissionRequest)
  const isSlideshow = computed<boolean>(() => surfaceVuexStore?.getters.isSlideshow)

  // Actions

  /**
   * Open the search bar.
   * To use where updating xSearchBar directly is not possible (e.g. in a Vue 2 component).
   */
  function openSearchBar(): void {
    xSearchBar.value = true
  }

  function updateSearchBarTop(top: number): void {
    searchBarTop.value = top
  }

  function refresh({ isAutoRefresh }: { isAutoRefresh?: boolean }): void {
    void surfaceVuexStore?.dispatch('refresh', { isAutoRefresh })
  }

  function changeConnectivity(isOnline: boolean): void {
    void surfaceVuexStore?.dispatch('changeConnectivity', { isOnline })
  }

  function hideReportPanel(): void {
    void surfaceVuexStore?.dispatch('hideReportPanel')
  }

  function hideDevelopersPanel(): void {
    void surfaceVuexStore?.dispatch('hideDevelopersPanel')
  }

  function showOverFileSizeModal(file): void {
    void surfaceVuexStore?.dispatch('showOverFileSizeModal', { file }, { root: true })
  }

  return {
    // State
    xSearchBar,
    isSearchBarInEditMode,
    searchBarTop,
    isRefreshPending,

    // Getters
    isScreenshotMode,
    isOverlayVisible,
    isUserHostingZoomCollaboration,
    wallId,
    xHeader,
    headerHeight,
    background,
    isGrid,
    isMap,
    isShelf,
    isSectioned,
    isStream,
    isTimeline,
    isTimelineV1,
    isTimelineV2,
    isCanvas,
    isCustomBackground,
    isBackgroundBlurred,
    isTitleBarTransparent,
    backgroundColor,
    skinAccentColorAsRGB,
    skinPrimaryColorAsRGB,
    skinPrimaryVariantColorAsRGB,
    skinAccentTextColor,
    backgroundLuminance,
    colorScheme,
    isReadOnly,
    isEmbedded,
    dir,
    fonts,
    fontId,
    viz,
    format,
    newPostLocation,
    address,
    builder,
    title,
    description,
    portrait,
    wallAttributes,
    mapThemeId,
    filterProfanity,
    domainName,
    namespace,
    googleApiKeyForDrive,
    publicKey,
    permalink,
    links,
    canUseSections,
    isOnline,
    xReportPanel,
    reportedPostId,
    xAuthor,
    xCommentsUnderPosts,
    isUsedInEducationalContext,
    wallCreatedAt,
    wallUpdatedAt,
    user,
    xFilterProfanitySetting,
    allLayouts,
    allLayoutsByGroup,
    postSize,
    isPostSizeWide,
    isPostSizeStandard,
    breakoutSectionId,
    isSectionBreakout,
    isSubmissionRequest,
    isSlideshow,

    // Actions
    openSearchBar,
    updateSearchBarTop,
    refresh,
    changeConnectivity,
    hideReportPanel,
    hideDevelopersPanel,
    showOverFileSizeModal,
  }
})
